/**
 * Quota management and error handling for IndexedDB storage
 * Handles quota exceeded errors and provides cleanup strategies
 */

import { initializeDB } from "./db";

export interface QuotaInfo {
  usage: number;
  quota: number;
  percentUsed: number;
  available: number;
}

/**
 * Check if error is a quota exceeded error
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const errorName = error.name?.toLowerCase() || "";
  const errorMessage = error.message?.toLowerCase() || "";

  return (
    errorName === "quotaexceedederror" ||
    errorMessage.includes("quota") ||
    errorMessage.includes("storage") ||
    errorMessage.includes("disk space")
  );
}

/**
 * Get current storage quota info
 */
export async function getStorageQuota(): Promise<QuotaInfo | null> {
  if (!(navigator.storage && navigator.storage.estimate)) {
    console.warn("[QuotaHandler] Storage API not available");
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;

    return {
      usage,
      quota,
      percentUsed: quota > 0 ? (usage / quota) * 100 : 0,
      available: quota - usage,
    };
  } catch (error) {
    console.error("[QuotaHandler] Error getting storage quota:", error);
    return null;
  }
}

/**
 * Check if storage is nearly full (>80%)
 */
export async function isStorageNearlyFull(): Promise<boolean> {
  const quota = await getStorageQuota();
  return quota ? quota.percentUsed > 80 : false;
}

/**
 * Handle quota exceeded error by cleaning up old data
 */
export async function handleQuotaExceeded(): Promise<void> {
  console.log("[QuotaHandler] Handling quota exceeded error...");

  try {
    // Get current quota info
    const quotaBefore = await getStorageQuota();
    console.log("[QuotaHandler] Storage before cleanup:", quotaBefore);

    // Clear IndexedDB caches to free up space
    const db = await initializeDB();

    // Clear old cached data from all stores
    const tx = db.transaction(
      ["cached-customers", "cached-products", "cached-services", "cached-projects"],
      "readwrite"
    );

    await Promise.all([
      tx.objectStore("cached-customers").clear(),
      tx.objectStore("cached-products").clear(),
      tx.objectStore("cached-services").clear(),
      tx.objectStore("cached-projects").clear(),
    ]);

    await tx.done;
    console.log("[QuotaHandler] Cleared all cached data");

    // Check quota after cleanup
    const quotaAfter = await getStorageQuota();
    console.log("[QuotaHandler] Storage after cleanup:", quotaAfter);

    if (quotaAfter && quotaAfter.percentUsed > 90) {
      console.warn("[QuotaHandler] Still low on storage after cleanup");
      throw new Error("Storage quota exceeded - please free up space or clear app data");
    }
  } catch (error) {
    console.error("[QuotaHandler] Error handling quota exceeded:", error);
    throw error;
  }
}

/**
 * Wrap an operation with quota error handling
 */
export async function withQuotaHandling<T>(
  operation: () => Promise<T>,
  retryOnQuotaError = true
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (isQuotaExceededError(error) && retryOnQuotaError) {
      console.log("[QuotaHandler] Quota exceeded, attempting cleanup and retry...");

      try {
        await handleQuotaExceeded();
        // Retry operation after cleanup
        return await operation();
      } catch (cleanupError) {
        console.error("[QuotaHandler] Cleanup failed:", cleanupError);
        throw new Error("Storage quota exceeded and cleanup failed. Please free up storage space.");
      }
    }
    throw error;
  }
}

/**
 * Log storage usage for debugging
 */
export async function logStorageUsage(): Promise<void> {
  const quota = await getStorageQuota();

  if (quota) {
    console.log("[QuotaHandler] Storage Usage:", {
      used: `${(quota.usage / 1024 / 1024).toFixed(2)} MB`,
      total: `${(quota.quota / 1024 / 1024).toFixed(2)} MB`,
      percentUsed: `${quota.percentUsed.toFixed(1)}%`,
      available: `${(quota.available / 1024 / 1024).toFixed(2)} MB`,
    });
  }
}

/**
 * Request persistent storage to prevent eviction
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!(navigator.storage && navigator.storage.persist)) {
    console.warn("[QuotaHandler] Persistent storage API not available");
    return false;
  }

  try {
    const isPersisted = await navigator.storage.persist();
    console.log("[QuotaHandler] Persistent storage:", isPersisted ? "granted" : "denied");
    return isPersisted;
  } catch (error) {
    console.error("[QuotaHandler] Error requesting persistent storage:", error);
    return false;
  }
}
